import React, { useState, useEffect } from 'react';
import { LogOut, Package, Settings, BarChart3, Users, FileText, UserCog, ShoppingCart, ClipboardList, DollarSign, X, Menu } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import ProductList from '../components/admin/ProductList';
import ProductForm from '../components/admin/ProductForm';
import QuoteList from '../components/admin/QuoteList';
import CustomerList from '../components/admin/CustomerList';
import CustomerForm from '../components/admin/CustomerForm';
import QuoteForm from '../components/admin/QuoteForm';
import DashboardStats from '../components/admin/DashboardStats';
import DashboardCharts from '../components/admin/DashboardCharts';
import RecentActivity from '../components/admin/RecentActivity';
import Login from '../components/admin/Login';
import UserList from '../components/admin/UserList';
import UserForm from '../components/admin/UserForm';
import PurchaseList from '../components/admin/PurchaseList';
import PurchaseForm from '../components/admin/PurchaseForm';
import OrderList from '../components/admin/OrderList';
import OrderForm from '../components/admin/OrderForm';
import SaleList from '../components/admin/SaleList';
import SaleForm from '../components/admin/SaleForm';
import { Product } from '../types/product';
import { Customer } from '../types/customer';
import { PurchaseWithProduct } from '../types/purchase';
import { OrderWithDetails } from '../types/order';
import { SaleWithDetails } from '../types/sale';
import { authService, User } from '../services/authService';
import { notificationService } from '../services/notificationService';
import { AdminUser, adminUserService } from '../services/adminUserService';
import { supabase } from '../lib/supabase';

type AdminView = 'dashboard' | 'products' | 'add-product' | 'edit-product' | 'quotes' | 'customers' | 'add-customer' | 'edit-customer' | 'create-quote' | 'edit-quote' | 'users' | 'add-user' | 'edit-user' | 'purchases' | 'add-purchase' | 'edit-purchase' | 'orders' | 'add-order' | 'edit-order' | 'sales' | 'add-sale' | 'edit-sale';

const Admin = () => {
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState<AdminView>('dashboard');
  const [previousView, setPreviousView] = useState<AdminView>('dashboard');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedQuoteCustomer, setSelectedQuoteCustomer] = useState<Customer | null>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [selectedPurchase, setSelectedPurchase] = useState<PurchaseWithProduct | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<OrderWithDetails | null>(null);
  const [selectedSale, setSelectedSale] = useState<SaleWithDetails | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  useEffect(() => {
    let isMounted = true;
    
    // Obtener sesión actual directamente de Supabase
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (isMounted) {
          if (session?.user) {
            // Usuario ya está autenticado, obtener rol
            let userRole = 'admin';
            if (session.user.email) {
              try {
                const isSuperAdmin = await adminUserService.isSuperAdmin(session.user.email);
                userRole = isSuperAdmin ? 'super_admin' : 'admin';
              } catch (roleError) {
                console.warn('Error al obtener rol de usuario, usando rol por defecto:', roleError);
                userRole = 'admin';
              }
            }
            
            setUser({
              id: session.user.id,
              email: session.user.email || '',
              role: userRole
            });
          } else {
            setUser(null);
          }
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error al obtener sesión inicial:', error);
        if (isMounted) {
          setUser(null);
          setIsLoading(false);
        }
      }
    };
    
    getInitialSession();

    // Escuchar cambios en la autenticación
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (isMounted) {
        if (session?.user) {
          // Obtener el rol del usuario desde la tabla admin_users
          let userRole = 'admin';
          if (session.user.email) {
            try {
              const isSuperAdmin = await adminUserService.isSuperAdmin(session.user.email);
              userRole = isSuperAdmin ? 'super_admin' : 'admin';
            } catch (roleError) {
              console.warn('Error al obtener rol de usuario en onAuthStateChange, usando rol por defecto:', roleError);
              userRole = 'admin';
            }
          }

          setUser({
            id: session.user.id,
            email: session.user.email || '',
            role: userRole
          });
        } else {
          setUser(null);
        }
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);


  const handleLogin = (loggedInUser: User) => {
    setUser(loggedInUser);
  };

  const handleLogout = async () => {
    if (window.confirm('¿Está seguro de que desea cerrar sesión?')) {
      const { error } = await authService.signOut();
      if (error) {
        notificationService.error('Error al cerrar sesión', 'Ha ocurrido un error al cerrar la sesión. Por favor, intente nuevamente.');
      } else {
        notificationService.success('Sesión cerrada', 'Has cerrado tu sesión correctamente.');
        setUser(null);
      }
    }
  };

  const navigateToForm = (targetView: AdminView) => {
    setPreviousView(currentView);
    setCurrentView(targetView);
  };

  const handleEditProduct = (product: Product) => {
    setSelectedProduct(product);
    navigateToForm('edit-product');
  };

  const handleAddProduct = () => {
    setSelectedProduct(null);
    navigateToForm('add-product');
  };

  const handleSaveProduct = () => {
    setSelectedProduct(null);
    setCurrentView('products');
  };

  const handleCancelForm = () => {
    setSelectedProduct(null);
    setSelectedCustomer(null);
    setSelectedUser(null);
    setSelectedPurchase(null);
    setSelectedOrder(null);
    setSelectedSale(null);
    setCurrentView(previousView);
  };

  const handleEditCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    navigateToForm('edit-customer');
  };

  const handleAddCustomer = () => {
    setSelectedCustomer(null);
    navigateToForm('add-customer');
  };

  const handleEditUser = (user: AdminUser) => {
    setSelectedUser(user);
    navigateToForm('edit-user');
  };

  const handleAddUser = () => {
    setSelectedUser(null);
    navigateToForm('add-user');
  };

  const handleSaveUser = () => {
    setSelectedUser(null);
    setCurrentView('users');
  };

  const handleSaveCustomer = () => {
    setSelectedCustomer(null);
    setCurrentView('customers');
  };

  const handleGenerateQuote = (customer: Customer) => {
    setSelectedQuoteCustomer(customer);
    navigateToForm('create-quote');
  };

  const handleCreateQuote = () => {
    setSelectedQuoteCustomer(null);
    navigateToForm('create-quote');
  };

  const handleAddPurchase = () => {
    setSelectedPurchase(null);
    navigateToForm('add-purchase');
  };

  const handleEditPurchase = (purchase: PurchaseWithProduct) => {
    setSelectedPurchase(purchase);
    navigateToForm('edit-purchase');
  };

  const handleSavePurchase = () => {
    setSelectedPurchase(null);
    setCurrentView('purchases');
  };

  const handleAddOrder = () => {
    setSelectedOrder(null);
    navigateToForm('add-order');
  };

  const handleEditOrder = (order: OrderWithDetails) => {
    setSelectedOrder(order);
    navigateToForm('edit-order');
  };

  const handleSaveOrder = () => {
    setSelectedOrder(null);
    setCurrentView('orders');
  };

  const handleAddSale = () => {
    setSelectedSale(null);
    navigateToForm('add-sale');
  };

  const handleEditSale = (sale: SaleWithDetails) => {
    setSelectedSale(sale);
    navigateToForm('edit-sale');
  };

  const handleSaveSale = () => {
    setSelectedSale(null);
    setCurrentView('sales');
  };

  // Mostrar pantalla de carga solo al inicio, no si ya hay una sesión activa
  if (isLoading && !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Mostrar login solo si no hay usuario autenticado y no está cargando
  if (!user && !isLoading) {
    return <Login onLogin={handleLogin} />;
  }

  const renderContent = () => {
    switch (currentView) {
      case 'dashboard':
        return (
          <div className="space-y-4 sm:space-y-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Panel de Administración</h2>
            
            {/* Stats Cards */}
            <DashboardStats />
            
            {/* Charts */}
            <DashboardCharts />
            
            {/* Quick Actions */}
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Acciones Rápidas</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <Button
                  onClick={() => setCurrentView('products')}
                  className="btn-primary h-auto p-3 sm:p-4 flex flex-col items-center"
                >
                  <Package className="w-6 h-6 sm:w-8 sm:h-8 mb-2" />
                  <span className="text-sm sm:text-base">Gestionar Productos</span>
                </Button>
               
                <Button
                  variant="outline"
                  className="h-auto p-3 sm:p-4 flex flex-col items-center"
                  onClick={() => setCurrentView('quotes')}
                >
                  <FileText className="w-6 h-6 sm:w-8 sm:h-8 mb-2" />
                  <span className="text-sm sm:text-base">Ver Cotizaciones</span>
                </Button>
                
                <Button
                  variant="outline"
                  className="h-auto p-3 sm:p-4 flex flex-col items-center"
                  onClick={() => setCurrentView('customers')}
                >
                  <Users className="w-6 h-6 sm:w-8 sm:h-8 mb-2" />
                  <span className="text-sm sm:text-base">Gestionar Clientes</span>
                </Button>

                <Button
                  variant="outline"
                  className="h-auto p-3 sm:p-4 flex flex-col items-center"
                  onClick={() => setCurrentView('purchases')}
                >
                  <ShoppingCart className="w-6 h-6 sm:w-8 sm:h-8 mb-2" />
                  <span className="text-sm sm:text-base">Gestionar Compras</span>
                </Button>
                
                <Button
                  variant="outline"
                  className="h-auto p-3 sm:p-4 flex flex-col items-center"
                  onClick={() => setCurrentView('orders')}
                >
                  <ClipboardList className="w-6 h-6 sm:w-8 sm:h-8 mb-2" />
                  <span className="text-sm sm:text-base">Gestionar Órdenes</span>
                </Button>
                
                <Button
                  variant="outline"
                  className="h-auto p-3 sm:p-4 flex flex-col items-center"
                  onClick={() => setCurrentView('sales')}
                >
                  <DollarSign className="w-6 h-6 sm:w-8 sm:h-8 mb-2" />
                  <span className="text-sm sm:text-base">Gestionar Ventas</span>
                </Button>
              </div>
            </div>
            
            {/* Recent Activity */}
            <RecentActivity />
          </div>
        );

      case 'products':
        return (
          <ProductList 
            onEdit={handleEditProduct} 
            onAdd={handleAddProduct}
          />
        );

      case 'add-product':
      case 'edit-product':
        return (
          <ProductForm
            product={selectedProduct}
            onSave={handleSaveProduct}
            onCancel={handleCancelForm}
          />
        );

      case 'quotes':
        return <QuoteList onCreateQuote={handleCreateQuote} />;

      case 'customers':
        return (
          <CustomerList
            onEdit={handleEditCustomer}
            onAdd={handleAddCustomer}
            onGenerateQuote={handleGenerateQuote}
          />
        );

      case 'add-customer':
      case 'edit-customer':
        return (
          <CustomerForm
            customer={selectedCustomer}
            onSave={handleSaveCustomer}
            onCancel={handleCancelForm}
          />
        );

      case 'create-quote':
        return (
          <QuoteForm
            customer={selectedQuoteCustomer}
            onSave={() => setCurrentView('quotes')}
            onCancel={handleCancelForm}
          />
        );

      case 'edit-quote':
        return (
          <QuoteForm
            customer={selectedQuoteCustomer}
            onSave={() => setCurrentView('quotes')}
            onCancel={handleCancelForm}
          />
        );

      case 'users':
        return (
          <UserList
            onEdit={handleEditUser}
            onAdd={handleAddUser}
          />
        );

      case 'add-user':
      case 'edit-user':
        return (
          <UserForm
            user={selectedUser}
            onSave={handleSaveUser}
            onCancel={handleCancelForm}
          />
        );

      case 'purchases':
        return (
          <PurchaseList
            onAdd={handleAddPurchase}
            onEdit={handleEditPurchase}
          />
        );

      case 'add-purchase':
      case 'edit-purchase':
        return (
          <PurchaseForm
            purchase={selectedPurchase}
            onSave={handleSavePurchase}
            onCancel={handleCancelForm}
          />
        );

      case 'orders':
        return (
          <OrderList
            onAdd={handleAddOrder}
            onEdit={handleEditOrder}
            onGenerateSale={handleAddSale}
          />
        );

      case 'add-order':
      case 'edit-order':
        return (
          <OrderForm
            order={selectedOrder}
            onSave={handleSaveOrder}
            onCancel={handleCancelForm}
          />
        );

      case 'sales':
        return (
          <SaleList
            onAdd={handleAddSale}
            onEdit={handleEditSale}
          />
        );

      case 'add-sale':
      case 'edit-sale':
        return (
          <SaleForm
            sale={selectedSale}
            onSave={handleSaveSale}
            onCancel={handleCancelForm}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Left Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 w-72 sm:w-80 bg-white shadow-lg border-r border-gray-200 transform transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-4 sm:p-6 h-full flex flex-col max-h-screen">
          {/* Header with Logo and Close Button */}
          <div className="flex items-center justify-between mb-6 sm:mb-8">
            <button
              onClick={() => navigate('/')}
              className="hover:opacity-80 transition-opacity flex items-center"
              title="Ir a la página principal"
            >
              <img
                src="/logo.png"
                alt="Atomic Corporation"
                className="h-12 sm:h-16 w-auto"
              />
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* User Info */}
          <div className="mb-6 sm:mb-8 p-3 sm:p-4 bg-gray-50 rounded-lg">
            <h3 className="text-xs sm:text-sm font-medium text-gray-500 mb-2">Usuario</h3>
            <p className="text-xs sm:text-sm text-gray-900 truncate">{user?.email}</p>
          </div>

          {/* Navigation */}
          <nav className="space-y-1 sm:space-y-2 flex-1 overflow-y-auto">
            <h3 className="text-xs sm:text-sm font-medium text-gray-500 mb-2 sm:mb-3 px-2 sm:px-4">Navegación</h3>
            <button
              onClick={() => {
                setCurrentView('dashboard');
                setIsMobileMenuOpen(false);
              }}
              className={`w-full text-left px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
                currentView === 'dashboard'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center space-x-2 sm:space-x-3">
                <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Dashboard</span>
              </div>
            </button>
            <button
              onClick={() => {
                setCurrentView('products');
                setIsMobileMenuOpen(false);
              }}
              className={`w-full text-left px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
                currentView === 'products' || currentView === 'add-product' || currentView === 'edit-product'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center space-x-2 sm:space-x-3">
                <Package className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Productos</span>
              </div>
            </button>
            <button
              onClick={() => {
                setCurrentView('quotes');
                setIsMobileMenuOpen(false);
              }}
              className={`w-full text-left px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
                currentView === 'quotes' || currentView === 'create-quote' || currentView === 'edit-quote'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center space-x-2 sm:space-x-3">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Cotizaciones</span>
              </div>
            </button>
            <button
              onClick={() => {
                setCurrentView('customers');
                setIsMobileMenuOpen(false);
              }}
              className={`w-full text-left px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
                currentView === 'customers' || currentView === 'add-customer' || currentView === 'edit-customer'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center space-x-2 sm:space-x-3">
                <Users className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Clientes</span>
              </div>
            </button>
            <button
              onClick={() => {
                setCurrentView('purchases');
                setIsMobileMenuOpen(false);
              }}
              className={`w-full text-left px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
                currentView === 'purchases' || currentView === 'add-purchase' || currentView === 'edit-purchase'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center space-x-2 sm:space-x-3">
                <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Compras</span>
              </div>
            </button>
            <button
              onClick={() => {
                setCurrentView('orders');
                setIsMobileMenuOpen(false);
              }}
              className={`w-full text-left px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
                currentView === 'orders' || currentView === 'add-order' || currentView === 'edit-order'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center space-x-2 sm:space-x-3">
                <ClipboardList className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Órdenes</span>
              </div>
            </button>
            <button
              onClick={() => {
                setCurrentView('sales');
                setIsMobileMenuOpen(false);
              }}
              className={`w-full text-left px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
                currentView === 'sales' || currentView === 'add-sale' || currentView === 'edit-sale'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center space-x-2 sm:space-x-3">
                <DollarSign className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Ventas</span>
              </div>
            </button>
          </nav>

          {/* Users Management */}
          <div className="mt-auto pt-4 sm:pt-6 border-t border-gray-200 space-y-2 sm:space-y-3">
            <Button
              onClick={() => {
                setCurrentView('users');
                setIsMobileMenuOpen(false);
              }}
              variant="outline"
              className="w-full justify-start text-blue-600 hover:text-blue-700 hover:bg-blue-50 text-xs sm:text-sm py-2"
            >
              <UserCog className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
              Usuarios
            </Button>
            
            {/* Logout */}
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50 text-xs sm:text-sm py-2"
            >
              <LogOut className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-4 sm:p-6 lg:p-8 lg:ml-0 min-h-screen">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between mb-4">
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            <Menu className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">Panel de Administración</h1>
          <div className="w-9"></div> {/* Spacer for centering */}
        </div>
        
        <div className="max-w-6xl mx-auto">
          {renderContent()}
        </div>
      </main>

      {/* Mobile Bottom Navigation - Simplified */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-30">
        <div className="grid grid-cols-5 gap-1 p-1">
          <button
            onClick={() => setCurrentView('dashboard')}
            className={`flex flex-col items-center py-2 px-1 rounded-lg ${
              currentView === 'dashboard'
                ? 'text-primary-600'
                : 'text-gray-600'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            <span className="text-xs mt-1">Inicio</span>
          </button>
          <button
            onClick={() => setCurrentView('products')}
            className={`flex flex-col items-center py-2 px-1 rounded-lg ${
              currentView === 'products' || currentView === 'add-product' || currentView === 'edit-product'
                ? 'text-primary-600'
                : 'text-gray-600'
            }`}
          >
            <Package className="w-4 h-4" />
            <span className="text-xs mt-1">Productos</span>
          </button>
          <button
            onClick={() => setCurrentView('quotes')}
            className={`flex flex-col items-center py-2 px-1 rounded-lg ${
              currentView === 'quotes' || currentView === 'create-quote' || currentView === 'edit-quote'
                ? 'text-primary-600'
                : 'text-gray-600'
            }`}
          >
            <FileText className="w-4 h-4" />
            <span className="text-xs mt-1">Cotiz</span>
          </button>
          <button
            onClick={() => setCurrentView('orders')}
            className={`flex flex-col items-center py-2 px-1 rounded-lg ${
              currentView === 'orders' || currentView === 'add-order' || currentView === 'edit-order'
                ? 'text-primary-600'
                : 'text-gray-600'
            }`}
          >
            <ClipboardList className="w-4 h-4" />
            <span className="text-xs mt-1">Órdenes</span>
          </button>
          <button
            onClick={() => setCurrentView('sales')}
            className={`flex flex-col items-center py-2 px-1 rounded-lg ${
              currentView === 'sales' || currentView === 'add-sale' || currentView === 'edit-sale'
                ? 'text-primary-600'
                : 'text-gray-600'
            }`}
          >
            <DollarSign className="w-4 h-4" />
            <span className="text-xs mt-1">Ventas</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Admin;